import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react';
import moment from 'moment';
import {
  selectPane, closePane, setPaneUser, setPaneTrack, setPaneHorizon,
  fetchPaneSeries, fetchPaneLeague, fetchPaneHorizons,
} from '../../slices/performancePaneSlice';
import { selectResources } from '../../slices/userSlice';
import {
  selectAuthUser, selectAuthIsForecaster, selectAuthIsAdmin,
  selectAuthIsMarketOwner, selectAuthIsMarketOwnerReadOnly,
} from '../../slices/authSlice';
import { selectBlurSensitive } from '../../slices/preferencesSlice';
import { SLIDE_OVER_BACKDROP_CLASS, SLIDE_OVER_PANEL_TRANSITION_CLASS, SLIDE_OVER_TOP_OFFSET_STYLE } from '../slideOverTransition';
import { paneFetchKey, forecasterOptions, userRankSeries, humanizeResourceName } from './paneData';
import PaneHeader from './PaneHeader';
import PaneControls from './PaneControls';
import EvolutionChartCard from './EvolutionChartCard';
import StandingsCard from './StandingsCard';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

// Mirrors the real layout (standings rows + two plot cards) in a soft tint so
// the first load reads as the page settling, not as stray grey blocks.
const PaneSkeleton = () => (
  <div className="animate-pulse" data-testid="pane-skeleton">
    <div className="h-2.5 w-24 rounded bg-gray-100 dark:bg-gray-800/70" />
    <div className="mt-4 space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-5 rounded bg-gray-100 dark:bg-gray-800/70" />
      ))}
    </div>
    <div className="mt-8 h-56 rounded-lg bg-gray-100 dark:bg-gray-800/70" />
    <div className="mt-4 h-56 rounded-lg bg-gray-100 dark:bg-gray-800/70" />
  </div>
);

const PerformancePane = ({ availableHorizons }) => {
  const dispatch = useDispatch();
  const pane = useSelector(selectPane);
  const resourcesRaw = useSelector(selectResources);
  const resources = useMemo(() => resourcesRaw || [], [resourcesRaw]);
  const currentUser = useSelector(selectAuthUser);
  const isForecaster = useSelector(selectAuthIsForecaster);
  const isAdmin = useSelector(selectAuthIsAdmin);
  const isMarketOwner = useSelector(selectAuthIsMarketOwner);
  const isMarketOwnerReadOnly = useSelector(selectAuthIsMarketOwnerReadOnly);
  const blurSensitive = useSelector(selectBlurSensitive);

  const canPick = isAdmin || isMarketOwner || isMarketOwnerReadOnly;
  const effectiveUserId = isForecaster ? String(currentUser?.id ?? '') : pane.userId;
  const fetchKey = paneFetchKey(pane);

  // The pane spans all markets, so its horizon options are the union of every
  // resource's available horizons, not the dashboard's per-resource list (a
  // D+1-only resource selected on the dashboard would otherwise hide the
  // other markets' horizons). Fetch each resource's list once per session.
  useEffect(() => {
    if (!pane.open) return;
    resources.forEach((resource) => {
      if (pane.byResource[resource.id]?.horizonsStatus) return;
      dispatch(fetchPaneHorizons({ resourceId: resource.id }));
    });
  }, [dispatch, pane.open, resources, pane.byResource]);

  const paneHorizons = useMemo(() => {
    const union = new Set();
    resources.forEach((resource) => {
      (pane.byResource[resource.id]?.horizons || []).forEach((h) => union.add(h));
    });
    // Until the per-resource lists land, fall back to the dashboard's list so
    // the switcher does not blink empty on open.
    const list = union.size > 0 ? [...union] : (availableHorizons || []);
    return [...list].sort((a, b) => a - b);
  }, [resources, pane.byResource, availableHorizons]);

  // Seed the horizon once horizons arrive if the pane opened before they
  // loaded; otherwise the skeleton would sit forever on a null horizon.
  useEffect(() => {
    if (pane.open && pane.horizonOffset == null && paneHorizons.length > 0) {
      dispatch(setPaneHorizon(paneHorizons[0]));
    }
  }, [dispatch, pane.open, pane.horizonOffset, paneHorizons]);

  // Fan out one full-field series fetch per resource missing the current key.
  useEffect(() => {
    if (!pane.open || pane.horizonOffset == null || !pane.year) return;
    const startDate = moment({ year: pane.year, month: pane.month - 1, day: 1 }).format('YYYY-MM-DD');
    const endDate = moment().format('YYYY-MM-DD');
    resources.forEach((resource) => {
      const e = pane.byResource[resource.id];
      // 'error' counts as settled so a rejected fetch is not retried on every
      // render; the user must hit Retry, which dispatches a fresh request.
      const fresh = e && e.seriesKey === fetchKey
        && (e.seriesStatus === 'ok' || e.seriesStatus === 'loading' || e.seriesStatus === 'forbidden' || e.seriesStatus === 'error');
      if (fresh) return;
      dispatch(fetchPaneSeries({
        resourceId: resource.id,
        track: pane.track,
        horizonOffset: pane.horizonOffset,
        startDate,
        endDate,
        fetchKey,
      }));
    });
  }, [dispatch, pane.open, pane.track, pane.horizonOffset, pane.year, pane.month, fetchKey, resources, pane.byResource]);

  // League status per resource for the selected user.
  useEffect(() => {
    if (!pane.open || !effectiveUserId || !pane.year) return;
    resources.forEach((resource) => {
      const e = pane.byResource[resource.id];
      if (!e || e.seriesStatus !== 'ok') return;
      // 'error' counts as settled, same rationale as the series effect above.
      const fresh = e.leagueUserId === effectiveUserId && e.leagueHorizon === pane.horizonOffset
        && (e.leagueStatus === 'ok' || e.leagueStatus === 'loading' || e.leagueStatus === 'error');
      if (fresh) return;
      dispatch(fetchPaneLeague({
        resourceId: resource.id, userId: effectiveUserId, year: pane.year, month: pane.month,
        horizonOffset: pane.horizonOffset,
      }));
    });
  }, [dispatch, pane.open, effectiveUserId, pane.year, pane.month, pane.horizonOffset, pane.byResource, resources]);

  const okEntries = useMemo(
    () => resources
      .map((resource, index) => ({ resource, index, data: pane.byResource[resource.id] }))
      .filter((x) => x.data && x.data.seriesStatus === 'ok' && x.data.seriesKey === fetchKey),
    [resources, pane.byResource, fetchKey]
  );
  const errorEntries = useMemo(
    () => resources
      .map((resource, index) => ({ resource, index, data: pane.byResource[resource.id] }))
      .filter((x) => x.data && x.data.seriesStatus === 'error'),
    [resources, pane.byResource]
  );
  const anyLoading = useMemo(
    () => resources.some((r) => pane.byResource[r.id]?.seriesStatus === 'loading')
      || (pane.horizonOffset != null && resources.some((r) => !pane.byResource[r.id])),
    [resources, pane.byResource, pane.horizonOffset]
  );

  // Markets where the selected user actually appears this month.
  const visibleResources = useMemo(
    () => okEntries.filter((x) => effectiveUserId && userRankSeries(x.data.series, effectiveUserId).length > 0),
    [okEntries, effectiveUserId]
  );

  const pickerOptions = useMemo(
    () => forecasterOptions(okEntries.map((x) => x.data.series), blurSensitive),
    [okEntries, blurSensitive]
  );

  const roleLabel = isAdmin ? 'superuser view' : (canPick ? 'market owner view' : null);

  const retry = (resourceId) => {
    const startDate = moment({ year: pane.year, month: pane.month - 1, day: 1 }).format('YYYY-MM-DD');
    dispatch(fetchPaneSeries({
      resourceId, track: pane.track, horizonOffset: pane.horizonOffset,
      startDate, endDate: moment().format('YYYY-MM-DD'), fetchKey,
    }));
  };

  // Bottom scroll-fade affordance: on short screens the standings + plots
  // overflow the body; the gradient signals "more below" and hides once the
  // user reaches the end (or when the content already fits).
  const scrollRef = useRef(null);
  const [showFade, setShowFade] = useState(false);
  const updateFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) { setShowFade(false); return; }
    setShowFade(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  }, []);
  useEffect(() => {
    updateFade();
    window.addEventListener('resize', updateFade);
    return () => window.removeEventListener('resize', updateFade);
  }, [updateFade, visibleResources, pane.mode, pane.unit, pane.open, anyLoading, effectiveUserId]);

  // Stale-while-revalidate: remember the last rendered markets so a horizon or
  // track change keeps the current plots on screen (dimmed) while the new data
  // loads, instead of flashing the skeleton on every switch.
  const lastVisibleRef = useRef([]);
  useEffect(() => {
    if (visibleResources.length > 0) lastVisibleRef.current = visibleResources;
  }, [visibleResources]);
  const isRefreshing = anyLoading && visibleResources.length === 0
    && lastVisibleRef.current.length > 0 && !!effectiveUserId;
  const displayResources = visibleResources.length > 0
    ? visibleResources
    : (isRefreshing ? lastVisibleRef.current : []);

  // Keep the forecaster picker's options from emptying (and its label blinking
  // to the placeholder) while a horizon/track switch refetches the field.
  const lastOptionsRef = useRef([]);
  useEffect(() => {
    if (pickerOptions.length > 0) lastOptionsRef.current = pickerOptions;
  }, [pickerOptions]);
  const displayOptions = pickerOptions.length > 0 ? pickerOptions : lastOptionsRef.current;

  // Keep the Dialog mounted while closed: unmounting it (early return null)
  // skips Headless UI's enter transition on open and drops the leave
  // transition on close entirely, so the pane pops instead of sliding.
  return (
    <Dialog open={pane.open} onClose={() => dispatch(closePane())} transition className="relative z-50">
      <DialogBackdrop
        transition
        className={SLIDE_OVER_BACKDROP_CLASS}
      />
      <div className="fixed inset-0 z-10 flex justify-end pointer-events-none" style={SLIDE_OVER_TOP_OFFSET_STYLE}>
        <DialogPanel
          transition
          className={`pointer-events-auto relative h-full w-full max-w-4xl bg-surface dark:bg-gray-870 shadow-xl border-l border-gray-200/70 dark:border-gray-600/70 flex flex-col ${SLIDE_OVER_PANEL_TRANSITION_CLASS}`}
        >
          <div className="flex-shrink-0 border-b border-slate-900/10 dark:border-gray-100/10">
            <PaneHeader
              canPick={canPick}
              options={displayOptions}
              userId={pane.userId}
              year={pane.year}
              month={pane.month}
              marketCount={displayResources.length}
              roleLabel={roleLabel}
              onSelectUser={(id) => dispatch(setPaneUser(id))}
              onClose={() => dispatch(closePane())}
            />
            <PaneControls
              track={pane.track}
              onSelectTrack={(t) => dispatch(setPaneTrack(t))}
              horizonOffset={pane.horizonOffset}
              onSelectHorizon={(h) => dispatch(setPaneHorizon(h))}
              availableHorizons={paneHorizons}
            />
          </div>
          <div ref={scrollRef} onScroll={updateFade} className="flex-1 overflow-y-auto px-5 sm:px-14 pt-6 pb-16 space-y-4">
            {canPick && !effectiveUserId ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 pt-8 text-center">
                Select a forecaster to see their cross-market performance.
              </p>
            ) : pane.horizonOffset == null || (anyLoading && displayResources.length === 0) ? (
              <PaneSkeleton />
            ) : displayResources.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 pt-8 text-center">
                No submissions scored yet in {MONTHS[(pane.month || 1) - 1]}.
              </p>
            ) : (
              <div>
                <StandingsCard pane={pane} userId={effectiveUserId} visibleResources={displayResources} />
                <div className="mt-6 border-t border-slate-900/10 dark:border-gray-100/10 pt-6">
                  <EvolutionChartCard pane={pane} userId={effectiveUserId} visibleResources={displayResources} />
                </div>
              </div>
            )}
            {errorEntries.map(({ resource }) => (
              <div key={resource.id} className="flex items-center justify-between rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-700 dark:text-red-300">
                <span>Failed to load {humanizeResourceName(resource.name)}</span>
                <button type="button" onClick={() => retry(resource.id)} className="font-semibold underline">
                  Retry
                </button>
              </div>
            ))}
          </div>
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-surface to-transparent dark:from-gray-870 transition-opacity duration-200 ${showFade ? 'opacity-100' : 'opacity-0'}`}
          />
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default PerformancePane;
